import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import { ApplicationPage, ApplicationSectionHeader, ApplicationStateRegion, ApplicationSurface, ApplicationViewHeader } from "../../../components/application";
import ProjectFacilitatorNavigation from "../components/ProjectFacilitatorNavigation";
import { useProjectFacilitatorMember } from "../hooks/useProjectFacilitatorMembers";
import { projectMembershipRoleLabel } from "../utils/projectMembershipPresentation";
import ProgramEvidenceComponents from "../evidence/ProgramEvidenceComponents";
import { resolveProgramFacilitatePresentationAdapter } from "../evidence/programFacilitatePresentationAdapterRegistry";

const stateLabel = progress => progress?.applicabilityState === "completed" ? "Completed"
  : progress?.applicabilityState === "in_progress" ? "In progress"
    : progress?.currentAttempt || progress?.historicalAttempts?.length
      ? "Historical participant record · no current project participation"
      : "Participation not established";
const dateLabel = value => value ? new Date(value).toLocaleDateString() : "—";
const currentStepPresentation = currentStep => {
  if (!currentStep?.label && !currentStep?.phaseLabel) return null;
  return [currentStep.phaseLabel, currentStep.label].filter(Boolean).join(" — ");
};

const AttemptDetails = ({ attempt }) => {
  if (!attempt) return null;
  const presentedCurrentStep = currentStepPresentation(attempt.currentStep);
  return <dl className="av-project-member-detail__facts">
    <div><dt>Completion</dt><dd>{attempt.completionPercentage}%</dd></div>
    {presentedCurrentStep ? <div><dt>Current phase / step</dt><dd>{presentedCurrentStep}</dd></div> : null}
    <div><dt>Started</dt><dd>{dateLabel(attempt.startedAt)}</dd></div>
    <div><dt>Recent activity</dt><dd>{dateLabel(attempt.lastActivityAt)}</dd></div>
    {attempt.completedAt ? <div><dt>Completed</dt><dd>{dateLabel(attempt.completedAt)}</dd></div> : null}
  </dl>
};

export default function ProjectFacilitatorMemberDetail() {
  const { slug, memberRef } = useParams();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
  const { detail, attentionReviewsByProgram = {}, reflectionOverridesByProgram = {},
    engagementAdjustmentsByProgram = {},
    recordAttentionReview = async () => {}, recordReflectionOverride = async () => {},
    recordEngagementAdjustment = async () => {},
    loading, error } = useProjectFacilitatorMember({ apiUrl, slug, memberRef, token: user?.token });
  if (loading) return <ApplicationPage><ApplicationStateRegion message="Loading Project member..." status="loading" /></ApplicationPage>;
  if (error || !detail) return <ApplicationPage><ApplicationStateRegion message={error || "Project member is unavailable."} status="error" /></ApplicationPage>;
  const { member } = detail;
  const hasG3Facilitate = detail.programs.some(program => resolveProgramFacilitatePresentationAdapter(program)?.key === "ksu-building-your-clinical-ear.facilitate-presentation@1");
  return (
    <ApplicationPage className="av-project-member-detail" variant="workspace">
      <ApplicationViewHeader
        title={member.displayIdentity}
        description={member.email || detail.project.title}
        metadata={member.email ? <p>{detail.project.title}</p> : null}
      />
      <Link className="av-project-facilitator-overview__back" to={`/app/facilitate/project/${slug}/members`}>Back to Members</Link>
      <ProjectFacilitatorNavigation slug={slug} showCohortResults={hasG3Facilitate} />
      <ApplicationSurface className="av-project-member-detail__context" variant="nested">
        <p><strong>Project role:</strong> {projectMembershipRoleLabel(member.role)}</p>
        {Object.prototype.hasOwnProperty.call(member, "participantIdentifier") && member.participantIdentifier ? <p><strong>Participant ID:</strong> {member.participantIdentifier}</p> : null}
        {member.cohort?.name ? <p><strong>Cohort:</strong> {member.cohort.name}</p> : null}
        {member.group?.name ? <p><strong>Group:</strong> {member.group.name}</p> : null}
      </ApplicationSurface>
      <section aria-labelledby="member-programs-title">
        <ApplicationSectionHeader title="Program progress" titleId="member-programs-title" />
        <div className="av-project-member-detail__programs">
          {detail.programs.map(program => {
            const presentationAdapter = resolveProgramFacilitatePresentationAdapter(program);
            const ParticipantEvidence = presentationAdapter?.ParticipantEvidenceComponent;
            return <ApplicationSurface as="article" key={program.id} variant="nested">
              <h3>{program.title}</h3>
              {program.version ? <p className="av-project-facilitator-overview__metadata">Version {program.version}</p> : null}
              <p><strong>{stateLabel(program.progress)}</strong></p>
              {!presentationAdapter?.suppressDefaultAttemptDetails ? <AttemptDetails attempt={program.progress.currentAttempt} /> : null}
              {detail.evidenceByProgram?.[program.id] ? (ParticipantEvidence
                ? <ParticipantEvidence evidence={detail.evidenceByProgram[program.id]} progress={program.progress}
                  attentionReviews={attentionReviewsByProgram[program.id]}
                  reflectionOverrides={reflectionOverridesByProgram[program.id]}
                  engagementAdjustment={engagementAdjustmentsByProgram[program.id]}
                  onRecordAttentionReview={input => recordAttentionReview({ projectId: detail.project.id,
                    program, ...input })}
                  onRecordReflectionOverride={input => recordReflectionOverride({
                    projectId: detail.project.id, program, ...input })}
                  onRecordEngagementAdjustment={input => recordEngagementAdjustment({
                    projectId: detail.project.id, program, ...input })} />
                : <ProgramEvidenceComponents evidence={detail.evidenceByProgram[program.id]} />) : null}
              {presentationAdapter?.configuredProgress?.secondary && program.progress.currentAttempt ? <details><summary>{presentationAdapter.configuredProgress.summary}</summary><AttemptDetails attempt={program.progress.currentAttempt} /></details> : null}
              {program.progress.historicalAttempts.length ? (
                <details><summary>Historical attempts ({program.progress.historicalAttempts.length})</summary>{program.progress.historicalAttempts.map(attempt => <div className="av-project-member-detail__historical" key={attempt.attemptId}><AttemptDetails attempt={attempt} /></div>)}</details>
              ) : null}
            </ApplicationSurface>;
          })}
        </div>
      </section>
      {detail.historicalProgramAttempts.length ? (
        <section aria-labelledby="historical-programs-title"><ApplicationSectionHeader title="Historical Program versions" titleId="historical-programs-title" /><ApplicationSurface variant="nested"><ul>{detail.historicalProgramAttempts.map(attempt => <li key={attempt.attemptId}>{attempt.programSlug || "Previous Program version"} — {attempt.state === "completed" ? "Completed" : "In progress"}</li>)}</ul></ApplicationSurface></section>
      ) : null}
    </ApplicationPage>
  );
}
